import { useEffect, useMemo, useState } from "react";

// Icons
import { FiEye } from "react-icons/fi";

// Custom Component
import Table from "../../Components/Table/Table";
import VerificationModal from "../../Components/VerificationModal/VerificationModal";

// Firebase
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";
import Loader from "../../Components/Loader/Loader";

const DocumentVerification = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRole, setSelectedRole] = useState("Rider");

  const fetchData = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        collection(db, COLLECTION_NAME.users)
      );
      let dataList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt
            ? new Date(
                data.createdAt.seconds * 1000 +
                  data.createdAt.nanoseconds / 1000000
              )
            : null,
        };
      });

      // Filter by role and pending status
      dataList = dataList
        .filter(
          (user) =>
            user.role === selectedRole && user.documentStatus === "pending"
        )
        .sort((a, b) =>
          sortOrder === "desc"
            ? b.createdAt - a.createdAt
            : a.createdAt - b.createdAt
        );

      setData(dataList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data: ", error);
      setLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const openModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const columns = useMemo(
    () => [
      { Header: "Name", accessor: "fullName" },
      { Header: "Email Address", accessor: "email" },
      { Header: "Phone", accessor: "phoneNumber" },
      {
        Header: "Role",
        accessor: "role",
        Cell: ({ row }) => {
          return (
            <span>
              {["Rider", "Driver"].includes(row.original.role)
                ? "Driver"
                : row.original.role}
            </span>
          );
        },
      },
      { Header: "Status", accessor: "documentStatus" },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <div className="flex">
            <button
              className="text-primary hover:text-dazzledBlue text-lg"
              onClick={() => openModal(row.original)}
            >
              <FiEye />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const onVerify = (userId) => {
    setData((prevData) => prevData.filter((user) => user.id !== userId));
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  useEffect(() => {
    fetchData();
  }, [sortOrder, selectedRole]);

  return (
    <>
      <div className="px-1 sm:px-1 md-px-4 min-h-screen">
        <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
          Document Verification
        </h1>

        {/* Role Filter */}
        <div className="flex mb-4">
          <div className="flex items-center space-x-2 mr-4">
            <span className="text-gray-700 font-medium">Filter by Role:</span>
            <div className="flex space-x-1">
              <button
                className={`px-4 py-2 rounded-md ${
                  selectedRole === "Rider"
                    ? "bg-celadon text-primary"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => handleRoleChange("Rider")}
              >
                Driver
              </button>
              <button
                className={`px-4 py-2 rounded-md ${
                  selectedRole === "passenger"
                    ? "bg-celadon text-primary"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => handleRoleChange("passenger")}
              >
                Passenger
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : (
          data && (
            <div className="">
              <div className="">
                <Table columns={columns} data={data} />
              </div>

              <VerificationModal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                user={selectedUser}
                onVerify={onVerify}
              />
            </div>
          )
        )}
      </div>
    </>
  );
};

export default DocumentVerification;

